/**
 * CastVsStuckCurve - Mana curve of what a simulated hand actually resolved
 *
 * Splits the non-land cards the simulation saw into those it managed to cast and
 * those still rotting in hand, bucketed by mana value. Cast cards are bucketed by
 * the face that was actually paid for, stuck cards by their cheapest face — so an
 * adventure counts as its cheap half on both sides. Lands are excluded; they are
 * the fuel, not the payoff.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';

import type { CastEvent, SimCard } from '@/features/decks/lib/handSim';
import { cheapestMode } from '@/features/decks/lib/handSim';

// ============================================================================
// Types
// ============================================================================

export interface CastVsStuckCurveProps {
  cast: CastEvent[];
  stuck: SimCard[];
}

/** Everything at or above this mana value shares the last bucket. */
const TOP_BUCKET = 6;

// ============================================================================
// Component
// ============================================================================

export default function CastVsStuckCurve({ cast, stuck }: CastVsStuckCurveProps): ReactElement | null {
  const buckets = Array.from({ length: TOP_BUCKET + 1 }, () => ({ cast: 0, stuck: 0 }));

  for (const event of cast) {
    if (!event.card.isLand) buckets[Math.min(event.mode.cmc, TOP_BUCKET)].cast++;
  }
  for (const card of stuck) {
    if (!card.isLand) buckets[Math.min(cheapestMode(card).cmc, TOP_BUCKET)].stuck++;
  }

  const tallest = Math.max(...buckets.map((bucket) => bucket.cast + bucket.stuck));
  if (tallest === 0) return null;

  const castTotal = buckets.reduce((sum, bucket) => sum + bucket.cast, 0);
  const stuckTotal = buckets.reduce((sum, bucket) => sum + bucket.stuck, 0);

  return (
    <div className="space-y-1">
      <div className="flex h-16 items-end gap-1">
        {buckets.map((bucket, cmc) => {
          const total = bucket.cast + bucket.stuck;
          return (
            <div key={cmc} className="flex flex-1 flex-col items-center gap-0.5">
              <div
                className="flex w-full flex-col justify-end"
                style={{ height: `${(total / tallest) * 100}%` }}
                title={`Mana value ${cmc === TOP_BUCKET ? '6+' : cmc}: ${bucket.cast} cast, ${bucket.stuck} stuck`}
              >
                {bucket.stuck > 0 && <div className="w-full rounded-t-sm bg-zinc-600" style={{ flexGrow: bucket.stuck }} />}
                {bucket.cast > 0 && (
                  <div className={clsx('w-full bg-emerald-500', bucket.stuck === 0 && 'rounded-t-sm')} style={{ flexGrow: bucket.cast }} />
                )}
              </div>
              <span className="text-ash/70 text-[9px] tabular-nums">{cmc === TOP_BUCKET ? '6+' : cmc}</span>
            </div>
          );
        })}
      </div>

      <div className="text-ash flex items-center justify-center gap-3 text-[10px]">
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          {castTotal} cast
        </span>
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-zinc-600" />
          {stuckTotal} stuck in hand
        </span>
      </div>
    </div>
  );
}
